/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
/* this page used for company log and search component and user details and log option showing */
// System defined variables
import React, { useState, forwardRef, useEffect } from 'react'
import {
  useSetRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useRecoilState,
} from 'recoil'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import LogoutIcon from '@mui/icons-material/Logout'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { DialogActions } from '@mui/material'
import { DialogTitle } from '@material-ui/core'
import { useLocation, useNavigate } from 'react-router-dom'
import { useConfirm } from 'material-ui-confirm'

// Custom defined variables
import { ConfirmPopupRendererObject } from '../../components/custom/confirmPopupRenderer/ConfirmPopupRenderer'
//commented this due to difference in image with import and assets and used assets image
//import LogoutIcon from '@mui/icons-material/Logout'
import AvatarIcon from '../../components/base/avataricon/AvatarIcon'
import SearchBar from '../../components/custom/searchBar/SearchBar'
import nPSLogo from '../../../assets/static/Marston_NPS.svg'
import companyLogo from '../../../assets/static/companyLogo.svg'
import {
  NotificationDetails,
  axiosConfiguration,
  selectedContractDetails,
  mappedUserRoles,
  madeChanges,
  penalityChargeNoticeDetail,
  showSplashScreen,
  prefixSetupData,
  contractSetupData,
  contraventionSetupData,
  selectedCaseDetails,
  selectCaseDetailNavigationDetails,
  orgSetupData,
  contactAddressData,
  selectedMenuData,
  selectOrgNavigationDetails,
  faqSetupData,
  templateData,
  currencyAtom,
  runtimeTokenExpiredAtom,
  selectedContactAddress,
  tribunalAppealData,
  userDetail,
  userWentInactiveAtom,
  loggingOutAtom,
  isSidebarExpanded,
  accessibleUserForClampAndRemoval,
  accessibleUserForCrConfiguration,
  airaLayoutChanges,
  widgetState,
  myWorkflowSummarizationDialog,
  onHoldSummarizationDialog,
  pcnPageSummarizationDialog,
  caseNumber,
} from '../../components/recoil/atoms/atoms'
import {
  authenticationData,
  msalPolicyNameAtom,
} from '../../components/recoil/atoms/atomsWithLocalStorage'
import BellNotification from '../../components/base/bellNotification/BellNotification'
import TitleBarUser from '../../../assets/images/TitleBarUser.svg'
import Button from '../../components/base/button/Button'
import utils from '../../../utils/utils'
import { fetchDataSelector } from '../../components/recoil/selectors/selectors'
import APIEndPoints from '../../../models/api/apiEndpoints'
import './TitleBar.css'

function TitleBar({ msalInstance }) {
  const getCoreData = useRecoilValue(fetchDataSelector)
  const setNotifyDetails = useSetRecoilState(NotificationDetails)
  const [dataLogoutWarning, setDataLogoutWarning] = useState(false)
  const AuthenticationResult = useRecoilValue(authenticationData)
  const msalPolicyName = useRecoilValue(msalPolicyNameAtom)
  const config = useRecoilValue(axiosConfiguration)
  const userRolesData = useRecoilValue(mappedUserRoles)
  const hasChanges = useRecoilValue(madeChanges)
  const userData = useRecoilValue(userDetail)
  const isAiraLayoutChanges = useRecoilValue(airaLayoutChanges)
  const location = useLocation()
  const navigate = useNavigate()
  const confirmAlert = useConfirm()
  const [widgetStateValue, setWidgetState] = useRecoilState(widgetState)
  const selectedContract = useRecoilValue(selectedContractDetails)
  const isAbandonedVehicleContract =
    selectedContract?.contractType?.toString()?.toLowerCase() ===
    utils?.contractTypes?.abandonedVehicle
  const isOrgWidget =
    widgetStateValue?.type === utils?.widgetTypes?.organisation &&
    !!widgetStateValue?.data?.id

  // Role names that have access to search functionality
  const SEARCHABLE_ROLES = [
    'superadmin',
    'bomanager',
    'qcbouser',
    'bouser',
    'noticeprocessing',
    'client',
    'readonly',
    'clientreadonly',
    'ceomanager',
    'poundoperator',
    'poundmanager',
    'simpleadmin',
    'simplebouser',
    'tflenforcementviewer',
    'cctvreviewer',
  ]

  const NotificationRecords = []
  let inHomePage = false
  if (location.pathname === '/') inHomePage = true
  useEffect(() => {
    setNotifyDetails(NotificationRecords)
  }, [])

  const resetpenalityChargeNoticeDetail = useResetRecoilState(
    penalityChargeNoticeDetail
  )
  const resetAuthenticationData = useResetRecoilState(authenticationData)
  const resetShowSplashScreen = useResetRecoilState(showSplashScreen)
  const resetPrefixSetupData = useResetRecoilState(prefixSetupData)
  const resetcontractSetupData = useResetRecoilState(contractSetupData)

  const resetcontraventionSetupData = useResetRecoilState(
    contraventionSetupData
  )
  const resetSelectedCaseDetails = useResetRecoilState(selectedCaseDetails)
  const resetSelectCaseDetailNavigationDetails = useResetRecoilState(
    selectCaseDetailNavigationDetails
  )
  const resetSelectedContractDetails = useResetRecoilState(
    selectedContractDetails
  )
  const resetOrgSetupData = useResetRecoilState(orgSetupData)

  const resetContactAddressData = useResetRecoilState(contactAddressData)
  // const resetUserTokenDetails = useResetRecoilState(userTokenDetails)
  const resetSelectedMenuData = useResetRecoilState(selectedMenuData)
  const [getSelectedMenuData, setSelectedMenuData] =
    useRecoilState(selectedMenuData)
  const resetSelectOrgNavigationDetails = useResetRecoilState(
    selectOrgNavigationDetails
  )
  // const resetMappedUserRoles = useResetRecoilState(mappedUserRoles)
  // const resetAssignedContractRoles = useResetRecoilState(assignedContractRoles)
  // const resetAssignedDistinctRoles = useResetRecoilState(assignedDistinctRoles)
  const resetFaqSetupData = useResetRecoilState(faqSetupData)
  const resetTemplateData = useResetRecoilState(templateData)
  const resetSelectedContactAddress = useResetRecoilState(
    selectedContactAddress
  )
  const resetTribunalAppealData = useResetRecoilState(tribunalAppealData)
  const resetCurrencyAtom = useResetRecoilState(currencyAtom)
  const resetRuntimeTokenExpiredAtom = useResetRecoilState(
    runtimeTokenExpiredAtom
  )
  const resetUserWentInactiveAtom = useResetRecoilState(userWentInactiveAtom)
  const setLoggingOut = useSetRecoilState(loggingOutAtom)
  const setSidebarExpanded = useSetRecoilState(isSidebarExpanded)
  const setAccessibleUserForClampAndRemoval = useSetRecoilState(
    accessibleUserForClampAndRemoval
  )
  const resetMyWorkflowSummarizationDialog = useResetRecoilState(
    myWorkflowSummarizationDialog
  )
  const resetOnHoldSummarizationDialog = useResetRecoilState(
    onHoldSummarizationDialog
  )
  const resetPcnPageSummarizationDialog = useResetRecoilState(
    pcnPageSummarizationDialog
  )
  const resetCaseNumber = useResetRecoilState(caseNumber)
  const redirectFrom = localStorage?.getItem('redirectFrom')
  // reset only persisted atoms here
  function resetAllAtom() {
    resetpenalityChargeNoticeDetail()
    resetAuthenticationData()
    resetShowSplashScreen()
    resetPrefixSetupData()
    resetcontractSetupData()
    resetcontraventionSetupData()
    resetSelectedCaseDetails()
    resetSelectCaseDetailNavigationDetails()
    resetSelectedContractDetails()
    resetSelectedContractDetails()
    resetOrgSetupData()
    resetContactAddressData()
    // resetUserTokenDetails()
    resetSelectedMenuData()
    resetSelectOrgNavigationDetails()
    // resetMappedUserRoles()
    // resetMappedUserRoles() //notfoundissue
    // resetAssignedContractRoles()
    // resetAssignedDistinctRoles()
    resetFaqSetupData()
    resetTemplateData()
    resetSelectedContactAddress()
    resetTribunalAppealData()
    resetCurrencyAtom()
    resetRuntimeTokenExpiredAtom()
    resetUserWentInactiveAtom()
    setSidebarExpanded()
    setAccessibleUserForClampAndRemoval()
  }

  const [userLabel, setUserLabel] = useState({})

  // Logged User Profile update
  useEffect(() => {
    const nameStringArray = userData?.userName?.toString()?.split(/ (.*)/s, 2)
    setUserLabel(() => {
      return {
        firstName: nameStringArray[0],
        lastName: nameStringArray[1],
      }
    })
  }, [userData])

  // function to get the first letters of logged in user name
  /* istanbul ignore next */

  //used usememo to get the avatarIconText only on logged in user data change
  // const avatarIconText = useMemo(() => {
  //   return getAvatarIconText()
  // }, [authenticationData])

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = (event) => {
    !anchorEl ? setAnchorEl(event.currentTarget) : setAnchorEl(null)
  }

  React.useEffect(() => {
    const handleLogoutEvent = (event) => {
      if (event?.key === 'logout-event') {
        resetAllAtom() // to avoid the warning of state update on unmounted component
      }
    }

    window.addEventListener('storage', handleLogoutEvent)

    return () => {
      localStorage.removeItem('logout-event')
      window.removeEventListener('storage', handleLogoutEvent)
    }
  }, [])

  //confirmation pop open when click LogOut
  function onLogoutClickHandler() {
    setDataLogoutWarning(true)
  }
  const onViewAccount = () => {
    const url = 'https://myaccount.microsoft.com/?ref=MeControl'
    window.open(url, '_blank')
  }

  //when signOut click AD account signOut functionality
  const signOut = async () => {
    // not handling await, success/failure response due to this api is an background process.
    await getCoreData(
      APIEndPoints?.autoUnassign?.method,
      `${APIEndPoints?.autoUnassign?.url}`
    )
    // capture user logout activity - background process
    getCoreData(
      APIEndPoints?.captureUserActivity?.method,
      `${APIEndPoints?.captureUserActivity?.url}?isLogin=false`,
      null,
      null,
      null,
      true,
      utils?.apiDomainName?.privateLand?.name
    )
    // reset only persisted atoms here
    setLoggingOut(true)
    resetAllAtom()
    localStorage?.removeItem('redirectFrom')
    utils?.signOutTheUser(
      msalInstance,
      config,
      AuthenticationResult,
      msalPolicyName
    )
  }

  useEffect(() => {
    if (redirectFrom?.toString() === 'permit') {
      signOut()
    }
  }, [redirectFrom])

  // Ignoring this due to usage of using MuiUseConfirm which return promiseError on testing
  /* istanbul ignore next */
  const checkAllowNavigation = async () => {
    let returnValue = true
    returnValue = await confirmAlert({
      ...ConfirmPopupRendererObject(null, utils?.dataSaveWarnMessage),
    })
      .then(() => {
        return true
      })
      .catch(() => {
        return false
      })

    return returnValue
  }
  const onClickLogoHandler = async () => {
    resetMyWorkflowSummarizationDialog()
    resetOnHoldSummarizationDialog()
    resetPcnPageSummarizationDialog()
    resetCaseNumber()

    // Ignoring this due to usage of using MuiUseConfirm which return promiseError on testing
    /* istanbul ignore next */
    if (hasChanges) {
      if (await checkAllowNavigation()) {
        setAccessibleUserForClampAndRemoval()
        navigate('/')
      }
    } else {
      setAccessibleUserForClampAndRemoval()
      navigate('/')
    }
    setSelectedMenuData({
      menuLoadingIndex: 0,
      anchorEl: null,
      menuAnchorEl: null,
      menuindex: 0,
      openDrawer: true,
      openNavGroup: false,
      selectedMenuId: null,
      selectedNavsecItemId: null,
      selectedSubMenuId: null,
      projectType: null,
    })
    setWidgetState((value) => ({
      ...value,
      type: utils?.widgetTypes?.organisation,
      data: null,
    }))
  }
  const hideNotification = () =>
    ['summarymanager', 'summaryagent'].includes(
      userData?.role?.toString()?.toLowerCase()
    )
  return (
    <div
      className={`titleBar-root ${
        getSelectedMenuData?.projectType?.toString()?.toLowerCase() ===
          utils?.airaNames?.airaName?.toString()?.toLowerCase() ||
        isAiraLayoutChanges
          ? utils?.airaNames?.nonNPS?.toString()?.toLowerCase()
          : ''
      } ${inHomePage || isOrgWidget ? '' : 'removeLeftPadding'}`}
    >
      <div
        className={`titleBar-title ${
          inHomePage || isOrgWidget ? '' : 'companyLogoDiv'
        }`}
        data-testid={`${inHomePage ? '' : 'companyLogoDiv'}`}
        onClick={inHomePage ? () => {} : onClickLogoHandler}
      >
        {inHomePage || isOrgWidget ? (
          <img
            data-testid="marstonLogo"
            className="img-logo"
            src={nPSLogo}
            loading="lazy"
          />
        ) : (
          <img
            data-testid="marstonLogo"
            className="img-logo"
            src={companyLogo}
            loading="lazy"
            width="36"
            height="36"
          />
        )}
      </div>
      <div className={`titleBar-Search`}>
        {!isAbandonedVehicleContract &&
        userRolesData?.filter((roleName) =>
          SEARCHABLE_ROLES.includes(roleName?.toString()?.toLowerCase())
        )?.length > 0 ? (
          <SearchBar />
        ) : null}
      </div>
      <div className="userHeader">
        <div className="titleBar-icon">
          {hideNotification() ? null : (
            <div className="notificationDiv">
              <BellNotification />
            </div>
          )}

          <div className="titleBarUser-avatar">
            {Object?.keys(userLabel)?.length > 0 ? (
              <>
                <AvatarIcon
                  className="Main-Avatar"
                  altText={
                    userLabel?.firstName[0]?.toString()?.toUpperCase() +
                      userLabel?.lastName[0]?.toString()?.toUpperCase() || '  '
                  }
                  children={
                    userLabel?.firstName[0]?.toString()?.toUpperCase() +
                      userLabel?.lastName[0]?.toString()?.toUpperCase() || '  '
                  }
                />
                <div
                  className="usernamesection"
                  title={userLabel?.firstName + ' ' + userLabel?.lastName || ''}
                >
                  {userLabel?.firstName + ' ' + userLabel?.lastName || ''}
                </div>
              </>
            ) : null}
            <div
              onClick={handleClick}
              className="titleBarUserDropDownDiv"
              data-testid="test_userIconDropDown"
            >
              <img className="titleBarUserDropDown" src={TitleBarUser} />
            </div>
            <Menu
              anchorEl={anchorEl}
              id="userDropDownMenu"
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              autoFocus={false}
              transitionDuration={500}
            >
              <div onClick={onViewAccount}>
                <MenuItem onClick={handleClose}>
                  <VisibilityIcon className="titleBarUser" />
                  <div className="innerDiv">View Account</div>
                </MenuItem>
              </div>
              <div onClick={onLogoutClickHandler}>
                <MenuItem>
                  <LogoutIcon className="titleBarUser" />
                  <div className="innerDiv">Logout</div>
                </MenuItem>
              </div>
            </Menu>
          </div>
        </div>
      </div>
      {/* when LogOut click confirmation popup open */}
      <Dialog
        id="dialog_dataLogOutWarning"
        open={dataLogoutWarning}
        className="dataLogOutWarning"
      >
        <DialogTitle>Logout</DialogTitle>
        <DialogContent>
          <div className="logoutString">
            <div>Are you sure want to logout?</div>
            <div>It&apos;s a good idea to close all browser windows.</div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            Field={{
              fieldValue: 'No',
              fieldLabel: 'No',
              variant: 'secondary',
            }}
            className="saveButton"
            onClickHandler={() => {
              setDataLogoutWarning(false)
            }}
          />
          <Button
            Field={{
              fieldValue: 'Yes',
              fieldLabel: 'Yes',
              variant: 'primary',
            }}
            className="saveButton"
            onClickHandler={() => {
              setDataLogoutWarning(false) //closing the dialog here due to the autoUnAssign api may take time so that the popup may still opened
              signOut()
            }}
          />
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default forwardRef(TitleBar)
